import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { AvatarModule } from 'primeng/avatar';
import { InicialesPipe } from '@compartido/pipes/iniciales-pipe';
import { environment } from '@env';
import { RUTAS_APP } from '@app/rutas/app-rutas.const';
import { SesionServicio } from '@nucleo/sesion/sesion-servicio';
import { NavegacionServicio } from '@nucleo/navegacion/navegacion-servicio';
import { traducciones } from '@nucleo/i18n/traduccion-util';
import { TEXTOS_COMPARTIDO } from '@compartido/i18n/compartido-textos';
import { SelectorConjunto } from '@compartido/crud/componentes/selector-conjunto/selector-conjunto';
import { SelectorIdioma } from '../selector-idioma/selector-idioma';
import { CampanaAvisos } from '../campana-avisos/campana-avisos';

interface OpcionMenuUsuario {
  identificador: string;
  etiqueta: string;
  icono: string;
  accion: () => void;
}

@Component({
  selector: 'app-barra-superior',
  imports: [
    SelectorConjunto,
    SelectorIdioma,
    CampanaAvisos,
    AvatarModule,
    InicialesPipe,
  ],
  templateUrl: './barra-superior.html',
  styleUrl: './barra-superior.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BarraSuperior {
  private readonly servicioSesion = inject(SesionServicio);
  private readonly servicioNavegacion = inject(NavegacionServicio);
  private readonly router = inject(Router);

  private readonly contenedorMenuUsuario = viewChild<ElementRef<HTMLElement>>('contenedorMenuUsuario');

  protected readonly nombreAplicacion = environment.nombreAplicacion;
  protected readonly menuUsuarioAbierto = signal(false);
  protected readonly nombreUsuario = computed(() => this.servicioSesion.usuario()?.fullName ?? '');
  /** Debajo del nombre se muestra el usuario de acceso (el backend no maneja cargo). */
  protected readonly cargoUsuario = computed(() => this.servicioSesion.usuario()?.username ?? '');

  protected readonly t = traducciones(TEXTOS_COMPARTIDO);

  protected readonly opcionesMenuUsuario = computed<OpcionMenuUsuario[]>(() => [
    {
      identificador: 'mi-perfil',
      etiqueta: this.t().barraSuperior.miPerfil,
      icono: 'pi pi-user',
      accion: () => this.router.navigateByUrl(RUTAS_APP.FUNCIONALIDADES.absolutas.miPerfil()),
    },
    {
      identificador: 'cerrar-sesion',
      etiqueta: this.t().barraSuperior.cerrarSesion,
      icono: 'pi pi-sign-out',
      accion: () => this.router.navigateByUrl(RUTAS_APP.AUTENTICACION.absolutas.cerrarSesion()),
    },
  ]);

  @HostListener('document:click', ['$event'])
  protected alClicDocumento(evento: MouseEvent): void {
    const contenedor = this.contenedorMenuUsuario()?.nativeElement;
    if (this.menuUsuarioAbierto() && contenedor && !contenedor.contains(evento.target as Node)) {
      this.menuUsuarioAbierto.set(false);
    }
  }

  protected alternarMenuLateral(): void {
    this.servicioNavegacion.alternarMenuMovil();
  }

  protected irAlInicio(): void {
    this.router.navigateByUrl(RUTAS_APP.FUNCIONALIDADES.absolutas.inicio());
  }

  protected alternarMenuUsuario(): void {
    this.menuUsuarioAbierto.update((abierto) => !abierto);
  }

  protected seleccionarOpcion(opcion: OpcionMenuUsuario): void {
    this.menuUsuarioAbierto.set(false);
    opcion.accion();
  }
}
